import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import './ConceptSelection.css';

// ─── Sound Engine ─────────────────────────────────────────────────────────────
const AudioEngine = (() => {
  let ctx = null;

  const initCtx = () => {
    if (!ctx) ctx = new (window.AudioContext || window.webkitAudioContext)();
    if (ctx.state === 'suspended') ctx.resume();
    return ctx;
  };

  const warmUp = () => {
    const ac = initCtx();
    const buf = ac.createBuffer(1, 1, 22050);
    const src = ac.createBufferSource();
    src.buffer = buf;
    src.connect(ac.destination);
    src.start(0);
  };

  document.addEventListener('click', warmUp, { once: true });
  document.addEventListener('touchstart', warmUp, { once: true });

  const play = (type, isJunior = true) => {
    try {
      const ac = initCtx();
      const o = ac.createOscillator();
      const g = ac.createGain();
      o.connect(g);
      g.connect(ac.destination);

      const juniorSounds = {
        click:       { freq: [523, 659], dur: 0.08, wave: 'square',   vol: 0.15 },
        select:      { freq: [440, 880], dur: 0.12, wave: 'sine',     vol: 0.2  },
        deselect:    { freq: [880, 440], dur: 0.1,  wave: 'sine',     vol: 0.15 },
        open:        { freq: [262, 330, 392, 523], dur: 0.06, wave: 'triangle', vol: 0.18 },
        close:       { freq: [523, 392, 330, 262], dur: 0.06, wave: 'triangle', vol: 0.15 },
        xp:          { freq: [523, 659, 784, 1047], dur: 0.08, wave: 'sine', vol: 0.2 },
        achievement: { freq: [392, 523, 659, 784, 1047], dur: 0.07, wave: 'sine', vol: 0.25 },
        hover:       { freq: [800], dur: 0.04, wave: 'sine', vol: 0.06 },
        start:       { freq: [262, 330, 392, 523, 659, 784], dur: 0.06, wave: 'square', vol: 0.2 },
        navigate:    { freq: [440, 523], dur: 0.07, wave: 'square', vol: 0.12 },
      };

      // Senior: subtle, soft tones
      const seniorSounds = {
        click:       { freq: [480], dur: 0.06, wave: 'sine', vol: 0.07 },
        select:      { freq: [520, 660], dur: 0.08, wave: 'sine', vol: 0.1 },
        deselect:    { freq: [660, 520], dur: 0.07, wave: 'sine', vol: 0.08 },
        open:        { freq: [400, 500], dur: 0.07, wave: 'sine', vol: 0.1 },
        close:       { freq: [500, 400], dur: 0.06, wave: 'sine', vol: 0.08 },
        xp:          { freq: [440, 550], dur: 0.08, wave: 'sine', vol: 0.1 },
        achievement: { freq: [440, 550, 660], dur: 0.08, wave: 'sine', vol: 0.12 },
        hover:       { freq: [600], dur: 0.03, wave: 'sine', vol: 0.04 },
        start:       { freq: [400, 500, 600], dur: 0.07, wave: 'sine', vol: 0.12 },
        navigate:    { freq: [440], dur: 0.06, wave: 'sine', vol: 0.07 },
      };

      const sounds = isJunior ? juniorSounds : seniorSounds;
      const s = sounds[type] || sounds.click;
      const freqs = Array.isArray(s.freq) ? s.freq : [s.freq];
      o.type = s.wave;
      g.gain.setValueAtTime(s.vol, ac.currentTime);
      freqs.forEach((f, i) => o.frequency.setValueAtTime(f, ac.currentTime + i * s.dur));
      const totalDur = freqs.length * s.dur;
      g.gain.exponentialRampToValueAtTime(0.001, ac.currentTime + totalDur + 0.05);
      o.start(ac.currentTime);
      o.stop(ac.currentTime + totalDur + 0.1);
    } catch (e) {}
  };

  return { play };
})();

// ─── Floating Particles (Junior only) ────────────────────────────────────────
const Particle = ({ x, y, color }) => (
  <div
    className="float-particle"
    style={{ left: x, top: y, background: color, position: 'fixed', pointerEvents: 'none', zIndex: 0 }}
  />
);

// ─── JUNIOR UI (Grade 1–5) ────────────────────────────────────────────────────
const JuniorConceptSelection = ({
  user, concepts, selectedSubconcepts, subconceptsData,
  modalOpen, currentConcept, loadingSubs,
  xpGained, achievementUnlocked, tourActive, currentStep,
  showHelpTooltip, particles, screenFlash, comboCount, glowConcept,
  highlightRef, totalSteps,
  openConceptModal, closeModal, toggleSubconcept, toggleAllSubconcepts,
  getTotalSelected, startAssessment, startOnboarding,
  nextStep, prevStep, skipTour,
  setShowHelpTooltip, navigate,
}) => {
  const totalSelected = getTotalSelected();
  const progressPercent = Math.min(100, (totalSelected / 30) * 100);

  const getConceptIcon = (name) => ({
    'Mathematics': 'fa-calculator', 'Physics': 'fa-atom',
    'Chemistry': 'fa-flask', 'Biology': 'fa-dna',
    'Programming': 'fa-code', 'History': 'fa-landmark',
    'Geography': 'fa-globe', 'English': 'fa-book',
  }[name] || 'fa-graduation-cap');

  const getConceptColor = (id) => {
    const colors = ['#ff6b6b','#ffd700','#69ff69','#00d4ff','#a855f7','#ff9800','#ec4899','#06b6d4'];
    return colors[id % colors.length];
  };

  const levelInfo = totalSelected < 5
    ? { level: 1, title: 'Novice', next: 5 }
    : totalSelected < 10
    ? { level: 2, title: 'Apprentice', next: 10 }
    : totalSelected < 20
    ? { level: 3, title: 'Scholar', next: 20 }
    : { level: 4, title: 'Master', next: 30 };

  return (
    <div className="cs-wrapper cs-junior">
      <div className="scanlines" />
      {screenFlash && <div className="screen-flash" />}
      {particles.map(p => <Particle key={p.id} x={p.x} y={p.y} color={p.color} />)}
      <div className="grid-bg" />
      <div className="glow-orb glow-orb-1" />
      <div className="glow-orb glow-orb-2" />
      <div className="glow-orb glow-orb-3" />

      {xpGained > 0 && (
        <div className="toast-xp">
          <span className="toast-icon">⚡</span>
          +{xpGained} XP
          <span className="toast-sub">COMBO x{comboCount}</span>
        </div>
      )}
      {achievementUnlocked && (
        <div className="toast-achievement">
          <div className="ach-glow" />
          <span className="ach-icon">🏆</span>
          <div>
            <div className="ach-title">ACHIEVEMENT UNLOCKED</div>
            <div className="ach-sub">Scholar's Path — 10 Topics Selected!</div>
          </div>
        </div>
      )}
      {comboCount >= 3 && (
        <div className="combo-badge">
          <span>COMBO</span>
          <strong>x{comboCount}</strong>
        </div>
      )}

      <div className="cs-container">
        <div className="hud-header">
          <div className="hud-left">
            <div className="hud-emblem"><i className="fas fa-dragon" /></div>
            <div className="hud-titles">
              <div className="hud-game-title">QUEST BOARD</div>
              <div className="hud-sub">Select your challenges, warrior</div>
            </div>
          </div>
          <div className="hud-right">
            <div className="hud-stat">
              <div className="stat-label">LEVEL</div>
              <div className="stat-val level-val">{levelInfo.level}</div>
            </div>
            <div className="hud-divider" />
            <div className="hud-stat">
              <div className="stat-label">CLASS</div>
              <div className="stat-val">{levelInfo.title}</div>
            </div>
            <div className="hud-divider" />
            <div className="hud-stat xp-stat">
              <div className="stat-label">XP</div>
              <div className="stat-val xp-val"><i className="fas fa-bolt" />{totalSelected * 10}</div>
            </div>
          </div>
        </div>

        <div className="welcome-card">
          <div className="player-sprite">
            <i className="fas fa-user-ninja" />
            <div className="sprite-ring" />
          </div>
          <div className="player-info">
            <div className="player-name">{user?.name || 'Warrior'}</div>
            <div className="player-class">Grade {user?.grade}</div>
          </div>
          <div className="player-stats-mini">
            <div className="mini-stat">
              <span className="mini-label">TOPICS</span>
              <span className="mini-val">{totalSelected}</span>
            </div>
            <div className="level-ring-wrap">
              <svg viewBox="0 0 40 40" className="level-ring-svg">
                <circle cx="20" cy="20" r="16" className="ring-bg" />
                <circle cx="20" cy="20" r="16" className="ring-fill"
                  style={{
                    strokeDasharray: `${2 * Math.PI * 16}`,
                    strokeDashoffset: `${2 * Math.PI * 16 * (1 - (totalSelected % levelInfo.next) / levelInfo.next)}`,
                  }}
                />
              </svg>
              <span className="ring-label">Lv{levelInfo.level}</span>
            </div>
          </div>
        </div>

        <div className="progress-card">
          <div className="exp-header">
            <div className="exp-label-row">
              <span className="exp-icon">⚔️</span>
              <span className="exp-title">QUEST PROGRESS</span>
              <span className="exp-count">{totalSelected} / 30</span>
            </div>
            <div className="exp-bar-outer">
              <div className="exp-bar-track">
                <div className="exp-bar-fill" style={{ width: `${progressPercent}%` }} />
                <div className="exp-bar-shine" style={{ width: `${progressPercent}%` }} />
                {[5, 10, 20].map(mark => (
                  <div key={mark} className="exp-mark" style={{ left: `${(mark / 30) * 100}%` }} />
                ))}
              </div>
            </div>
            <div className="exp-footer-row">
              <span className="exp-next">Next: {levelInfo.title} → {levelInfo.level < 4 ? levelInfo.next : 30} topics</span>
              <button className={`start-btn ${totalSelected > 0 ? 'active' : ''}`}
                onClick={startAssessment}
                onMouseEnter={() => AudioEngine.play('hover', true)}>
                <i className="fas fa-play" /> START QUEST
                {totalSelected > 0 && <span className="btn-glow" />}
              </button>
            </div>
          </div>
        </div>

        {totalSelected > 0 && (
          <div className="summary-card">
            <div className="summary-header">
              <i className="fas fa-scroll" />
              <span>ACTIVE QUESTS</span>
              <span className="summary-badge">{totalSelected} SELECTED</span>
            </div>
            <div className="summary-list">
              {Object.entries(selectedSubconcepts).slice(0, 3).map(([conceptId, subs]) => {
                const concept = concepts.find(c => c.id === parseInt(conceptId));
                return (
                  <div key={conceptId} className="summary-item">
                    <span className="si-name" style={{ color: getConceptColor(parseInt(conceptId)) }}>
                      {concept?.name || 'Concept'}
                    </span>
                    <div className="si-tags">
                      {subs.slice(0, 3).map((s, i) => <span key={i} className="si-tag">{s.replace(/_/g, ' ')}</span>)}
                      {subs.length > 3 && <span className="si-more">+{subs.length - 3}</span>}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        <div className="concepts-section">
          <div className="section-header">
            <span className="sh-icon">🗺️</span>
            <span className="sh-title">AVAILABLE QUESTS</span>
            <span className="sh-hint">[ TAP TO EXPAND ]</span>
          </div>
          {concepts.length === 0 ? (
            <div className="loading-placeholder">
              <div className="pixel-loader"><span /><span /><span /></div>
              <p>Loading quests...</p>
            </div>
          ) : (
            <div className="concepts-grid">
              {concepts.map((concept) => {
                const selCount = selectedSubconcepts[concept.id]?.length || 0;
                const totCount = subconceptsData[concept.id]?.list?.length || 0;
                const color = getConceptColor(concept.id);
                const pct = totCount ? (selCount / totCount) * 100 : 0;
                return (
                  <div key={concept.id}
                    className={`concept-card ${selCount > 0 ? 'has-selection' : ''} ${glowConcept === concept.id ? 'glowing' : ''}`}
                    style={{ '--card-color': color }}
                    onClick={() => openConceptModal(concept)}
                    onMouseEnter={() => AudioEngine.play('hover', true)}>
                    <div className="card-scanline" />
                    <div className="card-corner tl" /><div className="card-corner tr" />
                    <div className="card-corner bl" /><div className="card-corner br" />
                    <div className="concept-icon-wrap">
                      <i className={`fas ${getConceptIcon(concept.name)}`} style={{ color }} />
                      {selCount > 0 && <span className="selection-bubble">{selCount}</span>}
                    </div>
                    <div className="concept-name">{concept.name}</div>
                    <div className="concept-desc">{concept.description || 'Click to explore'}</div>
                    <div className="card-progress-bar">
                      <div className="card-progress-fill" style={{ width: `${pct}%`, background: color }} />
                    </div>
                    <div className="card-footer-row">
                      <span className="card-topic-count"><i className="fas fa-layer-group" /> {totCount} topics</span>
                      {selCount > 0 && <span className="card-sel-count" style={{ color }}><i className="fas fa-check" /> {selCount} active</span>}
                    </div>
                    <div className="card-enter-hint">ENTER →</div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <button className="back-btn"
          onClick={() => { AudioEngine.play('navigate', true); navigate('/dashboard'); }}
          onMouseEnter={() => AudioEngine.play('hover', true)}>
          <i className="fas fa-arrow-left" /> ← BACK TO HUB
        </button>
      </div>

      {/* Modal */}
      {modalOpen && currentConcept && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal" onClick={e => e.stopPropagation()}
            style={{ '--modal-color': getConceptColor(currentConcept.id) }}>
            <div className="modal-scanline" />
            <div className="modal-corner mc-tl" /><div className="modal-corner mc-tr" />
            <div className="modal-header">
              <div className="modal-icon-wrap">
                <i className={`fas ${getConceptIcon(currentConcept.name)}`} />
              </div>
              <div className="modal-titles">
                <h2>{currentConcept.name}</h2>
                <p>{currentConcept.description || 'Select your battle topics'}</p>
              </div>
              <button className="modal-close" onClick={closeModal} onMouseEnter={() => AudioEngine.play('hover', true)}>
                <i className="fas fa-times" />
              </button>
            </div>
            <div className="modal-body">
              {loadingSubs ? (
                <div className="modal-loading">
                  <div className="pixel-loader"><span /><span /><span /></div>
                  <p>Loading topics...</p>
                </div>
              ) : subconceptsData[currentConcept.id] ? (
                subconceptsData[currentConcept.id].list?.length > 0 ? (
                  <>
                    <div className="select-all-row">
                      <label className="select-all"
                        onClick={() => { AudioEngine.play('click', true); toggleAllSubconcepts(currentConcept.id); }}>
                        <input type="checkbox" readOnly
                          checked={selectedSubconcepts[currentConcept.id]?.length === subconceptsData[currentConcept.id].list.length} />
                        <span className="pixel-check" />
                        <span>SELECT ALL TOPICS</span>
                      </label>
                      <div className="sel-count-badge">
                        {selectedSubconcepts[currentConcept.id]?.length || 0} / {subconceptsData[currentConcept.id].list.length}
                      </div>
                    </div>
                    <div className="subconcepts-grid">
                      {subconceptsData[currentConcept.id].list.map((sub, idx) => {
                        const isSel = selectedSubconcepts[currentConcept.id]?.includes(sub.name) || false;
                        return (
                          <div key={idx}
                            className={`subconcept-card ${isSel ? 'selected' : ''}`}
                            onClick={(e) => toggleSubconcept(currentConcept.id, sub.name, e)}
                            onMouseEnter={() => AudioEngine.play('hover', true)}
                            style={{ '--accent': getConceptColor(currentConcept.id) }}>
                            <div className="sub-check-icon">{isSel ? '▣' : '□'}</div>
                            <div className="sub-info">
                              <div className="sub-name">{sub.name.replace(/_/g, ' ')}</div>
                              <div className="sub-meta"><i className="fas fa-scroll" /> {sub.total_questions || 0} questions</div>
                            </div>
                            {sub.mastery_level && (
                              <div className={`mastery-badge ${sub.mastery_level > 70 ? 'high' : sub.mastery_level > 40 ? 'mid' : 'low'}`}>
                                {sub.mastery_level}%
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </>
                ) : (
                  <div className="error-state"><i className="fas fa-ghost" /><p>No topics found.</p></div>
                )
              ) : (
                <div className="error-state">
                  <i className="fas fa-skull-crossbones" />
                  <p>Failed to load. Retry?</p>
                  <button onClick={() => loadSubconcepts(currentConcept.id)}>⟳ RETRY</button>
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button className="cancel-btn" onClick={closeModal} onMouseEnter={() => AudioEngine.play('hover', true)}>← BACK</button>
              <button className="confirm-btn" onClick={closeModal} onMouseEnter={() => AudioEngine.play('hover', true)}>
                ✔ CONFIRM ({selectedSubconcepts[currentConcept.id]?.length || 0})
              </button>
            </div>
          </div>
        </div>
      )}

      <button className="help-btn"
        onClick={() => { AudioEngine.play('click', true); startOnboarding(); }}
        onMouseEnter={() => { setShowHelpTooltip(true); AudioEngine.play('hover', true); }}
        onMouseLeave={() => setShowHelpTooltip(false)}>
        ?
        {showHelpTooltip && <span className="help-tip">TAKE TOUR</span>}
      </button>

      {tourActive && (
        <>
          <div className="tour-backdrop" />
          <div ref={highlightRef} className="tour-highlight" />
          <div className="tour-card">
            <div className="tour-step-num">STEP {currentStep}/{totalSteps}</div>
            <div className="tour-title">
              {['Welcome, Warrior!', 'Track Your XP', 'Explore Quests', 'Begin Battle!'][currentStep - 1]}
            </div>
            <p className="tour-desc">
              {[
                'Select topics you want to master. Each selection earns XP!',
                'Watch your progress bar fill. Aim for 30 to reach Master rank!',
                'Click any quest card to view and pick its sub-topics.',
                'When ready, hit START QUEST to begin your assessment!',
              ][currentStep - 1]}
            </p>
            <div className="tour-btns">
              {currentStep > 1 && <button className="tb-prev" onClick={prevStep}>◀ PREV</button>}
              <button className="tb-next" onClick={nextStep}>{currentStep === totalSteps ? "⚔ LET'S GO!" : 'NEXT ▶'}</button>
              <button className="tb-skip" onClick={skipTour}>SKIP</button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

// ─── SENIOR UI (Grade 6–12) ───────────────────────────────────────────────────
const SeniorConceptSelection = ({
  user, concepts, selectedSubconcepts, subconceptsData,
  modalOpen, currentConcept, loadingSubs,
  tourActive, currentStep, showHelpTooltip, glowConcept,
  highlightRef, totalSteps,
  openConceptModal, closeModal, toggleSubconcept, toggleAllSubconcepts,
  getTotalSelected, startAssessment, startOnboarding,
  nextStep, prevStep, skipTour,
  setShowHelpTooltip, navigate, loadSubconcepts,
}) => {
  const totalSelected = getTotalSelected();
  const progressPercent = Math.min(100, (totalSelected / 30) * 100);

  const subjectMeta = {
    'Mathematics':  { icon: 'fa-square-root-alt', color: '#4f46e5' },
    'Physics':      { icon: 'fa-atom',            color: '#0891b2' },
    'Chemistry':    { icon: 'fa-flask',            color: '#059669' },
    'Biology':      { icon: 'fa-dna',             color: '#16a34a' },
    'Programming':  { icon: 'fa-code',            color: '#7c3aed' },
    'History':      { icon: 'fa-landmark',        color: '#b45309' },
    'Geography':    { icon: 'fa-globe',           color: '#0369a1' },
    'English':      { icon: 'fa-book-open',       color: '#9333ea' },
  };

  const getMeta = (name) => subjectMeta[name] || { icon: 'fa-graduation-cap', color: '#6366f1' };

  const subjectGroups = concepts.reduce((groups, concept) => {
    const category = ['Mathematics', 'Physics', 'Chemistry', 'Biology'].includes(concept.name)
      ? 'Science & Math'
      : ['Programming'].includes(concept.name)
      ? 'Technology'
      : ['History', 'Geography', 'English'].includes(concept.name)
      ? 'Humanities'
      : 'Other';
    if (!groups[category]) groups[category] = [];
    groups[category].push(concept);
    return groups;
  }, {});

  return (
    <div className="cs-wrapper cs-senior">
      {/* Top nav bar */}
      <div className="sn-navbar">
        <div className="sn-nav-inner">
          <button className="sn-back-btn"
            onClick={() => { AudioEngine.play('navigate', false); navigate('/dashboard'); }}
            onMouseEnter={() => AudioEngine.play('hover', false)}>
            <i className="fas fa-arrow-left" /> Dashboard
          </button>
          <div className="sn-nav-title">
            <i className="fas fa-books" />
            Topic Selection
          </div>
          <div className="sn-nav-right">
            <span className="sn-grade-badge">Grade {user?.grade}</span>
          </div>
        </div>
      </div>

      <div className="sn-container">
        {/* Header */}
        <div className="sn-page-header">
          <div className="sn-header-left">
            <h1 className="sn-page-title">Select Your Topics</h1>
            <p className="sn-page-subtitle">
              Choose the subjects and topics you want to be assessed on. You can select multiple topics across subjects.
            </p>
          </div>
          <div className="sn-header-actions">
            <button className={`sn-start-btn ${totalSelected > 0 ? 'sn-start-active' : ''}`}
              onClick={startAssessment}
              onMouseEnter={() => AudioEngine.play('hover', false)}>
              <i className="fas fa-play" />
              Begin Assessment
              {totalSelected > 0 && <span className="sn-topic-count">{totalSelected} selected</span>}
            </button>
          </div>
        </div>

        {/* Progress strip */}
        <div className="sn-progress-strip">
          <div className="sn-progress-info">
            <span className="sn-progress-label">Selection progress</span>
            <span className="sn-progress-fraction">{totalSelected} of 30 topics</span>
          </div>
          <div className="sn-progress-bar">
            <div className="sn-progress-fill" style={{ width: `${progressPercent}%` }} />
          </div>
          <div className="sn-progress-milestones">
            {[
              { at: 5, label: 'Starter' },
              { at: 10, label: 'Focused' },
              { at: 20, label: 'Advanced' },
              { at: 30, label: 'Complete' },
            ].map(m => (
              <div key={m.at}
                className={`sn-milestone ${totalSelected >= m.at ? 'sn-milestone-reached' : ''}`}
                style={{ left: `${(m.at / 30) * 100}%` }}>
                <div className="sn-milestone-dot" />
                <span className="sn-milestone-label">{m.label}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Selected summary pill row */}
        {totalSelected > 0 && (
          <div className="sn-selected-summary">
            <span className="sn-ss-label">
              <i className="fas fa-check-circle" /> Topics selected:
            </span>
            <div className="sn-ss-pills">
              {Object.entries(selectedSubconcepts).flatMap(([conceptId, subs]) =>
                subs.slice(0, 2).map((s, i) => {
                  const concept = concepts.find(c => c.id === parseInt(conceptId));
                  const meta = getMeta(concept?.name);
                  return (
                    <span key={`${conceptId}-${i}`} className="sn-pill"
                      style={{ '--pill-color': meta.color }}>
                      {s.replace(/_/g, ' ')}
                    </span>
                  );
                })
              ).slice(0, 6)}
              {totalSelected > 6 && <span className="sn-pill sn-pill-more">+{totalSelected - 6} more</span>}
            </div>
          </div>
        )}

        {/* Subject grid by group */}
        {concepts.length === 0 ? (
          <div className="sn-loading">
            <div className="sn-spinner" />
            <p>Loading subjects...</p>
          </div>
        ) : (
          <div className="sn-subjects-area">
            {Object.entries(subjectGroups).map(([groupName, groupConcepts]) => (
              <div key={groupName} className="sn-group">
                <div className="sn-group-label">{groupName}</div>
                <div className="sn-subject-grid">
                  {groupConcepts.map((concept) => {
                    const meta = getMeta(concept.name);
                    const selCount = selectedSubconcepts[concept.id]?.length || 0;
                    const totCount = subconceptsData[concept.id]?.list?.length || 0;
                    const pct = totCount ? (selCount / totCount) * 100 : 0;
                    return (
                      <div key={concept.id}
                        className={`sn-subject-card ${selCount > 0 ? 'sn-card-active' : ''} ${glowConcept === concept.id ? 'sn-card-open' : ''}`}
                        style={{ '--subject-color': meta.color }}
                        onClick={() => openConceptModal(concept)}
                        onMouseEnter={() => AudioEngine.play('hover', false)}>
                        <div className="sn-card-left">
                          <div className="sn-subject-icon">
                            <i className={`fas ${meta.icon}`} />
                          </div>
                          <div className="sn-card-info">
                            <div className="sn-subject-name">{concept.name}</div>
                            <div className="sn-subject-desc">{concept.description || 'Click to explore topics'}</div>
                          </div>
                        </div>
                        <div className="sn-card-right">
                          {selCount > 0 && (
                            <div className="sn-sel-info">
                              <span className="sn-sel-count" style={{ color: meta.color }}>
                                {selCount}/{totCount}
                              </span>
                              <div className="sn-mini-bar">
                                <div className="sn-mini-fill" style={{ width: `${pct}%`, background: meta.color }} />
                              </div>
                            </div>
                          )}
                          {selCount === 0 && totCount > 0 && (
                            <span className="sn-topic-hint">{totCount} topics</span>
                          )}
                          <i className="fas fa-chevron-right sn-chevron" />
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Senior Modal */}
      {modalOpen && currentConcept && (
        <div className="sn-modal-overlay" onClick={closeModal}>
          <div className="sn-modal" onClick={e => e.stopPropagation()}
            style={{ '--modal-color': getMeta(currentConcept.name).color }}>
            <div className="sn-modal-header">
              <div className="sn-modal-icon">
                <i className={`fas ${getMeta(currentConcept.name).icon}`} />
              </div>
              <div className="sn-modal-title-area">
                <h2>{currentConcept.name}</h2>
                <p>{currentConcept.description || 'Select topics to include in your assessment'}</p>
              </div>
              <button className="sn-modal-close" onClick={closeModal}>
                <i className="fas fa-times" />
              </button>
            </div>

            <div className="sn-modal-body">
              {loadingSubs ? (
                <div className="sn-loading"><div className="sn-spinner" /><p>Loading topics...</p></div>
              ) : subconceptsData[currentConcept.id] ? (
                subconceptsData[currentConcept.id].list?.length > 0 ? (
                  <>
                    <div className="sn-select-all-row">
                      <label className="sn-select-all-label"
                        onClick={() => { AudioEngine.play('click', false); toggleAllSubconcepts(currentConcept.id); }}>
                        <input type="checkbox" readOnly
                          checked={selectedSubconcepts[currentConcept.id]?.length === subconceptsData[currentConcept.id].list.length} />
                        Select all topics
                      </label>
                      <span className="sn-sel-fraction">
                        {selectedSubconcepts[currentConcept.id]?.length || 0} / {subconceptsData[currentConcept.id].list.length} selected
                      </span>
                    </div>
                    <div className="sn-topics-list">
                      {subconceptsData[currentConcept.id].list.map((sub, idx) => {
                        const isSel = selectedSubconcepts[currentConcept.id]?.includes(sub.name) || false;
                        return (
                          <div key={idx}
                            className={`sn-topic-row ${isSel ? 'sn-topic-selected' : ''}`}
                            onClick={(e) => toggleSubconcept(currentConcept.id, sub.name, e)}
                            onMouseEnter={() => AudioEngine.play('hover', false)}>
                            <div className="sn-topic-check">
                              {isSel
                                ? <i className="fas fa-check-square" style={{ color: getMeta(currentConcept.name).color }} />
                                : <i className="far fa-square" />}
                            </div>
                            <div className="sn-topic-info">
                              <span className="sn-topic-name">{sub.name.replace(/_/g, ' ')}</span>
                              <span className="sn-topic-meta">{sub.total_questions || 0} questions</span>
                            </div>
                            {sub.mastery_level !== undefined && (
                              <div className="sn-mastery-chip"
                                data-level={sub.mastery_level > 70 ? 'high' : sub.mastery_level > 40 ? 'mid' : 'low'}>
                                {sub.mastery_level}% mastery
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </>
                ) : (
                  <div className="sn-empty"><i className="fas fa-inbox" /><p>No topics available</p></div>
                )
              ) : (
                <div className="sn-empty">
                  <i className="fas fa-exclamation-circle" />
                  <p>Failed to load topics</p>
                  <button className="sn-retry-btn" onClick={() => loadSubconcepts(currentConcept.id)}>Retry</button>
                </div>
              )}
            </div>

            <div className="sn-modal-footer">
              <button className="sn-btn-secondary" onClick={closeModal}>Cancel</button>
              <button className="sn-btn-primary" onClick={closeModal}
                style={{ background: getMeta(currentConcept.name).color }}>
                Confirm ({selectedSubconcepts[currentConcept.id]?.length || 0} selected)
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Help button */}
      <button className="sn-help-btn"
        onClick={() => { AudioEngine.play('click', false); startOnboarding(); }}
        onMouseEnter={() => { setShowHelpTooltip(true); AudioEngine.play('hover', false); }}
        onMouseLeave={() => setShowHelpTooltip(false)}>
        <i className="fas fa-question" />
        {showHelpTooltip && <span className="sn-help-tip">Take a tour</span>}
      </button>

      {/* Tour */}
      {tourActive && (
        <>
          <div className="sn-tour-backdrop" />
          <div ref={highlightRef} className="sn-tour-highlight" />
          <div className="sn-tour-card">
            <div className="sn-tour-step">Step {currentStep} of {totalSteps}</div>
            <div className="sn-tour-title">
              {['Welcome', 'Track progress', 'Browse subjects', 'Start assessment'][currentStep - 1]}
            </div>
            <p className="sn-tour-desc">
              {[
                'Choose topics across subjects to personalise your assessment.',
                'The progress bar shows how many topics you have selected.',
                'Click any subject card to expand and select specific topics.',
                'When you are ready, click Begin Assessment to start.',
              ][currentStep - 1]}
            </p>
            <div className="sn-tour-btns">
              {currentStep > 1 && <button className="sn-tb-prev" onClick={prevStep}>Previous</button>}
              <button className="sn-tb-next" onClick={nextStep}>
                {currentStep === totalSteps ? "Let's begin" : 'Next'}
              </button>
              <button className="sn-tb-skip" onClick={skipTour}>Skip tour</button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

// ─── Main Component (Grade Router) ───────────────────────────────────────────
const ConceptSelection = () => {
  const navigate = useNavigate();

  const [concepts, setConcepts]                 = useState([]);
  const [selectedSubconcepts, setSelectedSubconcepts] = useState({});
  const [subconceptsData, setSubconceptsData]   = useState({});
  const [modalOpen, setModalOpen]               = useState(false);
  const [currentConcept, setCurrentConcept]     = useState(null);
  const [loadingSubs, setLoadingSubs]           = useState(false);
  const [xpGained, setXpGained]                 = useState(0);
  const [achievementUnlocked, setAchievementUnlocked] = useState(false);
  const [tourActive, setTourActive]             = useState(false);
  const [currentStep, setCurrentStep]           = useState(1);
  const [showHelpTooltip, setShowHelpTooltip]   = useState(false);
  const [particles, setParticles]               = useState([]);
  const [screenFlash, setScreenFlash]           = useState(false);
  const [comboCount, setComboCount]             = useState(0);
  const [lastSelectTime, setLastSelectTime]     = useState(0);
  const [glowConcept, setGlowConcept]           = useState(null);
  const [user, setUser]                         = useState(null);

  const highlightRef = useRef(null);
  const totalSteps   = 4;
  const comboTimer   = useRef(null);

  // Grade check: junior = 1–5, senior = 6–12
  const grade = parseInt(user?.grade) || 0;
  const isJunior = grade <= 5;

  useEffect(() => {
    loadConcepts();
    loadUserData();
    const done = localStorage.getItem('solveSmart_concept_tour');
    if (!done) setTimeout(() => startOnboarding(), 1200);
  }, []);

  useEffect(() => {
    if (!isJunior) return;
    const total = getTotalSelected();
    if (total > 0 && total % 5 === 0 && xpGained === 0) {
      setXpGained(50);
      AudioEngine.play('xp', true);
      spawnParticles(window.innerWidth / 2, window.innerHeight / 2, '#ffd700', 12);
      setTimeout(() => setXpGained(0), 2000);
    }
  }, [selectedSubconcepts, isJunior]);

  const spawnParticles = (x, y, color, count = 8) => {
    if (!isJunior) return;
    const ps = Array.from({ length: count }, (_, i) => ({
      id: Date.now() + i,
      x: x + (Math.random() - 0.5) * 200,
      y: y + (Math.random() - 0.5) * 200,
      color,
    }));
    setParticles(prev => [...prev, ...ps]);
    setTimeout(() => setParticles(prev => prev.filter(p => !ps.find(np => np.id === p.id))), 800);
  };

  const flashScreen = () => {
    if (!isJunior) return;
    setScreenFlash(true);
    setTimeout(() => setScreenFlash(false), 150);
  };

  const loadUserData = useCallback(async () => {
    try {
      const res = await fetch('/api/profile');
      const data = await res.json();
      if (data.success && data.user) {
        setUser({ name: data.user.name, grade: data.user.grade, avatar_emoji: data.user.avatar_emoji });
      }
    } catch (e) {}
  }, []);

  const loadConcepts = async () => {
    try {
      const res = await axios.get('/api/concepts');
      if (res.data.success) setConcepts(res.data.concepts);
    } catch (e) { console.error(e); }
  };

  const loadSubconcepts = async (conceptId) => {
    setLoadingSubs(true);
    try {
      const res = await axios.get(`/api/subconcepts/${conceptId}`);
      if (res.data.success && res.data.subconcepts) {
        setSubconceptsData(prev => ({
          ...prev,
          [conceptId]: {
            name: res.data.concept_name,
            list: res.data.subconcepts,
            progress: res.data.user_progress || {},
          },
        }));
        const saved = {};
        Object.entries(res.data.user_progress || {}).forEach(([name, prog]) => {
          if (prog.selected) {
            if (!saved[conceptId]) saved[conceptId] = [];
            saved[conceptId].push(name);
          }
        });
        setSelectedSubconcepts(prev => ({ ...prev, ...saved }));
      }
    } catch (e) { console.error(e); }
    finally { setLoadingSubs(false); }
  };

  const openConceptModal = async (concept) => {
    AudioEngine.play('open', isJunior);
    setCurrentConcept(concept);
    setModalOpen(true);
    setGlowConcept(concept.id);
    if (!subconceptsData[concept.id]) await loadSubconcepts(concept.id);
  };

  const closeModal = () => {
    AudioEngine.play('close', isJunior);
    setModalOpen(false);
    setCurrentConcept(null);
    setTimeout(() => setGlowConcept(null), 300);
  };

  const toggleSubconcept = async (conceptId, subconceptName, e) => {
    const isSelected = selectedSubconcepts[conceptId]?.includes(subconceptName) || false;
    const now = Date.now();

    if (isJunior) {
      if (now - lastSelectTime < 800) setComboCount(c => c + 1);
      else setComboCount(1);
      setLastSelectTime(now);
      if (comboTimer.current) clearTimeout(comboTimer.current);
      comboTimer.current = setTimeout(() => setComboCount(0), 1500);
      if (e) {
        const rect = e.currentTarget.getBoundingClientRect();
        spawnParticles(rect.left + rect.width / 2, rect.top + rect.height / 2, isSelected ? '#ff5252' : '#69ff69', 6);
      }
    }

    AudioEngine.play(isSelected ? 'deselect' : 'select', isJunior);

    setSelectedSubconcepts(prev => {
      const arr = prev[conceptId] || [];
      return { ...prev, [conceptId]: isSelected ? arr.filter(s => s !== subconceptName) : [...arr, subconceptName] };
    });

    try {
      await axios.post('/api/update-subconcept-selection', {
        concept_id: conceptId,
        subconcept_name: subconceptName,
        is_selected: !isSelected,
      });
      const totalSelected = getTotalSelected() + (!isSelected ? 1 : -1);
      if (totalSelected >= 10 && !achievementUnlocked) {
        setAchievementUnlocked(true);
        AudioEngine.play('achievement', isJunior);
        if (isJunior) {
          flashScreen();
          spawnParticles(window.innerWidth / 2, 100, '#a855f7', 20);
        }
        setTimeout(() => setAchievementUnlocked(false), 3000);
      }
    } catch (e) { console.error(e); }
  };

  const toggleAllSubconcepts = async (conceptId) => {
    AudioEngine.play('open', isJunior);
    const conceptData = subconceptsData[conceptId];
    if (!conceptData) return;
    const all = conceptData.list.map(s => s.name);
    const cur = selectedSubconcepts[conceptId] || [];
    const isAll = cur.length === all.length;
    setSelectedSubconcepts(prev => ({ ...prev, [conceptId]: isAll ? [] : all }));
    for (const sub of all) {
      await axios.post('/api/update-subconcept-selection', {
        concept_id: conceptId, subconcept_name: sub, is_selected: !isAll,
      });
    }
  };

  const getTotalSelected = () =>
    Object.values(selectedSubconcepts).reduce((t, arr) => t + arr.length, 0);

  const startAssessment = async () => {
    const total = getTotalSelected();
    if (total === 0) { AudioEngine.play('deselect', isJunior); alert('Please select at least one topic!'); return; }
    AudioEngine.play('start', isJunior);
    if (isJunior) {
      flashScreen();
      spawnParticles(window.innerWidth / 2, window.innerHeight / 2, '#ffd700', 30);
    }
    try {
      await axios.post('/api/save-subconcept-selection', { selections: selectedSubconcepts });
      setTimeout(() => navigate('/assessment'), 600);
    } catch (e) { console.error(e); alert('Failed to save. Try again.'); }
  };

  const startOnboarding = () => {
    setTourActive(true);
    setCurrentStep(1);
    document.body.style.overflow = 'hidden';
    setTimeout(() => highlightElement(1), 100);
  };

  const highlightElement = (step) => {
    const juniorSel = { 1: '.welcome-card', 2: '.progress-card', 3: '.concepts-grid', 4: '.start-btn' };
    const seniorSel = { 1: '.sn-page-header', 2: '.sn-progress-strip', 3: '.sn-subjects-area', 4: '.sn-start-btn' };
    const selectors = isJunior ? juniorSel : seniorSel;
    const el = document.querySelector(selectors[step]);
    if (el && highlightRef.current) {
      const r = el.getBoundingClientRect();
      Object.assign(highlightRef.current.style, {
        display: 'block',
        top: `${r.top - 10}px`, left: `${r.left - 10}px`,
        width: `${r.width + 20}px`, height: `${r.height + 20}px`,
      });
    }
  };

  const nextStep = () => {
    AudioEngine.play('click', isJunior);
    if (currentStep < totalSteps) {
      setCurrentStep(s => s + 1);
      setTimeout(() => highlightElement(currentStep + 1), 300);
    } else completeTour();
  };

  const prevStep = () => {
    AudioEngine.play('navigate', isJunior);
    if (currentStep > 1) {
      setCurrentStep(s => s - 1);
      setTimeout(() => highlightElement(currentStep - 1), 300);
    }
  };

  const skipTour = () => {
    AudioEngine.play('close', isJunior);
    setTourActive(false);
    document.body.style.overflow = 'auto';
    if (highlightRef.current) highlightRef.current.style.display = 'none';
    localStorage.setItem('solveSmart_concept_tour', 'true');
  };

  const completeTour = () => {
    AudioEngine.play('xp', isJunior);
    setTourActive(false);
    document.body.style.overflow = 'auto';
    if (highlightRef.current) highlightRef.current.style.display = 'none';
    localStorage.setItem('solveSmart_concept_tour', 'true');
  };

  const sharedProps = {
    user, concepts, selectedSubconcepts, subconceptsData,
    modalOpen, currentConcept, loadingSubs,
    tourActive, currentStep, showHelpTooltip, glowConcept,
    highlightRef, totalSteps,
    openConceptModal, closeModal, toggleSubconcept, toggleAllSubconcepts,
    getTotalSelected, startAssessment, startOnboarding,
    nextStep, prevStep, skipTour,
    setShowHelpTooltip, navigate, loadSubconcepts,
  };

  if (isJunior) {
    return <JuniorConceptSelection
      {...sharedProps}
      xpGained={xpGained}
      achievementUnlocked={achievementUnlocked}
      particles={particles}
      screenFlash={screenFlash}
      comboCount={comboCount}
    />;
  }

  return <SeniorConceptSelection {...sharedProps} />;
};

export default ConceptSelection;